/* =============================================================
   The bridge.

   One modal for the whole app, holding a stack of entities. Any
   component can say "show me batch 456" without importing the page
   that batches live on, and a view can drill into anything else
   the registry knows about.

     open(type, id, { tab })   start a fresh stack
     push(type, id, { tab })   drill in, keeping a way back
     back()                    pop one frame
     close()                   drop the stack entirely

   The stack lives in the URL (see stack.js), so `open` and `push`
   are history entries: the browser's back button pops a level for
   free, and a drilled-in view can be pasted to someone else.
   ============================================================= */

import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getWaypoint, resolveTab } from './registry';
import { searchWithStack, stackFromSearch, sameStack } from './stack';
import WaypointHost from './WaypointHost.jsx';

const WaypointContext = createContext(null);

export const useWaypoint = () => {
  const ctx = useContext(WaypointContext);
  if (!ctx) {
    // A hard failure rather than a silent no-op: a dead "open" link
    // that quietly does nothing is worse to track down than a stack
    // trace naming the provider.
    throw new Error('useWaypoint must be used inside <WaypointProvider>');
  }
  return ctx;
};

export default function WaypointProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  /* The URL is the source of truth. Deriving the stack from it on
     every render means the back button, a pasted link and a click
     all arrive through the same door -- there is no second copy of
     this state to drift. */
  const stack = useMemo(() => {
    const raw = stackFromSearch(location.search);
    // Drop frames for types nothing has registered. A link to a type
    // that has since been removed opens what it still can rather
    // than a blank modal.
    return raw.filter((f) => getWaypoint(f.type));
  }, [location.search]);

  const writeStack = useCallback(
    (next, { replace = false } = {}) => {
      if (sameStack(stack, next)) return;
      navigate(
        { pathname: location.pathname, search: searchWithStack(location.search, next) },
        { replace },
      );
    },
    [stack, location.pathname, location.search, navigate],
  );

  const open = useCallback(
    (type, id, opts = {}) => {
      if (!getWaypoint(type) || !id) return;
      writeStack([{ type, id: String(id), tab: resolveTab(type, opts.tab) }]);
    },
    [writeStack],
  );

  const push = useCallback(
    (type, id, opts = {}) => {
      if (!getWaypoint(type) || !id) return;
      const frame = { type, id: String(id), tab: resolveTab(type, opts.tab) };
      const top = stack[stack.length - 1];
      // Pushing what is already on top is a double click, not a
      // drill -- otherwise back would appear to do nothing once.
      if (top && top.type === frame.type && String(top.id) === frame.id) return;
      writeStack([...stack, frame]);
    },
    [stack, writeStack],
  );

  const back = useCallback(() => {
    if (stack.length <= 1) {
      writeStack([]);
      return;
    }
    writeStack(stack.slice(0, -1));
  }, [stack, writeStack]);

  const close = useCallback(() => writeStack([]), [writeStack]);

  /* Changing the tab replaces rather than pushes. A tab is a way of
     looking at one thing, not a different thing -- pushing would
     make back walk through every tab the reader glanced at before
     it left the entity. */
  const setTab = useCallback(
    (tab) => {
      if (stack.length === 0) return;
      const top = stack[stack.length - 1];
      const resolved = resolveTab(top.type, tab);
      if (!resolved || resolved === top.tab) return;
      writeStack([...stack.slice(0, -1), { ...top, tab: resolved }], { replace: true });
    },
    [stack, writeStack],
  );

  // Escape closes the whole thing, matching every other modal here.
  useEffect(() => {
    if (stack.length === 0) return undefined;
    const onKey = (e) => {
      // An edit form open over the popup (pg-modal--over) is the one an
      // Escape is meant for; the popup behind it stays.
      if (e.key === 'Escape' && !document.querySelector('.pg-modal--over')) close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stack.length, close]);

  const value = useMemo(
    () => ({ stack, open, push, back, close, setTab, isOpen: stack.length > 0 }),
    [stack, open, push, back, close, setTab],
  );

  return (
    <WaypointContext.Provider value={value}>
      {children}
      <WaypointHost />
    </WaypointContext.Provider>
  );
}
